// Supabase in the browser, for AUTHENTICATION ONLY.
//
// THE LINE THIS FILE MUST NOT CROSS (CLAUDE.md → Architecture): the browser never queries Postgres.
// supabase-js is here for the one job it is genuinely good at — holding a session, persisting it,
// and refreshing the access token before it expires. Data still flows web → Worker → service role,
// with the Worker deriving the owner from the token this client issues. There is no `.from()` call
// anywhere in apps/web, and a review that finds one has found a bug.
//
// CONFIGURATION IS ALL-OR-NOTHING. Three variables decide which app the owner is looking at:
//   none set   → demo mode: the fixed in-memory dataset, badge visible, no account needed
//   all three  → remote mode: sign in, then read and write the real database through the Worker
// A half-filled config is refused at build time by the guard in vite.config.ts, because a
// half-configured app fails in exactly the way data loss looks.

import { createClient, type SupabaseClient, type Session } from '@supabase/supabase-js'

export interface RemoteConfig {
  supabaseUrl: string
  supabaseAnonKey: string
  apiBaseUrl: string
}

/**
 * Read at CALL time, not at module load, so a test can stub the environment and so nothing depends
 * on import order.
 *
 * The anon key is public by design — it identifies the project, it does not authorize anything
 * beyond what RLS already allows an anonymous visitor to do (here: nothing). It still lives in a
 * gitignored env file, because the moment a key is in git nobody re-checks which kind it was.
 */
export function remoteConfig(): RemoteConfig | null {
  if (import.meta.env.VITE_DEMO_MODE === 'true') return null
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
  if (!supabaseUrl || !supabaseAnonKey || !apiBaseUrl) return null
  return { supabaseUrl, supabaseAnonKey, apiBaseUrl }
}

let client: SupabaseClient | null = null

/** The one client instance. Null in demo mode — there is nothing to sign in to. */
export function getSupabase(): SupabaseClient | null {
  const config = remoteConfig()
  if (!config) return null
  client ??= createClient(config.supabaseUrl, config.supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // Nothing in this app ever arrives back from an OAuth redirect: single user, password only.
      detectSessionInUrl: false,
    },
  })
  return client
}

/**
 * The bearer token for a Worker call, or null when nobody is signed in.
 *
 * `getSession()` returns the stored session and refreshes it first if it has expired, so a tab left
 * open overnight does not silently start failing every save at 401.
 */
export async function getAccessToken(): Promise<string | null> {
  const supabase = getSupabase()
  if (!supabase) return null
  const { data } = await supabase.auth.getSession()
  return data.session?.access_token ?? null
}

export type { Session }
